import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { PageHeader, EmptyState, inr } from "../components/UI";
import { CreditCard, AlertTriangle, Clock, ArrowRight } from "lucide-react";

const urgencyMeta = {
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-800", bar: "bg-red-500" },
  due_soon: { label: "Due Soon", cls: "bg-amber-100 text-amber-800", bar: "bg-amber-500" },
  upcoming: { label: "Upcoming", cls: "bg-slate-100 text-slate-700", bar: "bg-slate-400" },
};

export default function PaymentCollection() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/payment-collection/overdue")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? data.items : data.items.filter((i) => i.urgency === filter);

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="payment-collection-page">
      <PageHeader
        title="Payment Collection"
        subtitle="Track invoices with 15-day collection SLA — never miss a follow-up"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 stagger">
        <div className="card-flat p-5" data-testid="kpi-overdue">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Overdue</div>
            <AlertTriangle size={18} className="text-red-500" />
          </div>
          <div className="font-display font-bold text-3xl mt-2 text-red-600">{data.summary.overdue || 0}</div>
        </div>
        <div className="card-flat p-5" data-testid="kpi-due-soon">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Due in ≤5 days</div>
            <Clock size={18} className="text-amber-500" />
          </div>
          <div className="font-display font-bold text-3xl mt-2 text-amber-600">{data.summary.due_soon || 0}</div>
        </div>
        <div className="card-flat p-5" data-testid="kpi-balance">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Total at Risk</div>
            <CreditCard size={18} className="text-[#C1121F]" />
          </div>
          <div className="font-display font-bold text-2xl font-mono-num mt-2 text-[#C1121F]">{inr(data.summary.total_balance_due || 0)}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        {["all", "overdue", "due_soon", "upcoming"].map((f) => (
          <button key={f} data-testid={`pc-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${filter === f ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={CreditCard} title="All payments on track" hint="No invoices overdue. Great work on collections!" />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="pc-table">
            <thead>
              <tr>
                <th>Invoice #</th><th>Customer</th><th>Anchor</th><th>Due Date</th>
                <th className="text-right">Total</th><th className="text-right">Balance</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const meta = urgencyMeta[r.urgency];
                return (
                  <tr key={r.invoice_id} data-testid={`pc-row-${r.invoice_id}`}>
                    <td className="font-mono-num font-semibold text-[#C1121F]">{r.invoice_no}</td>
                    <td>{r.customer_name}</td>
                    <td className="text-xs text-[#475569]">
                      <div className="font-mono-num">{r.anchor_date}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{r.anchor_type}</div>
                    </td>
                    <td className="font-mono-num text-sm">
                      {r.due_date}
                      <div className={`text-[10px] font-semibold ${r.days_late > 0 ? "text-red-600" : "text-[#475569]"}`}>
                        {r.days_late > 0 ? `${r.days_late} days late` : r.days_late === 0 ? "due today" : `in ${-r.days_late} days`}
                      </div>
                    </td>
                    <td className="text-right font-mono-num">{inr(r.total)}</td>
                    <td className="text-right font-mono-num font-semibold text-[#C1121F]">{inr(r.balance)}</td>
                    <td><span className={`badge ${meta.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${meta.bar}`}></span>{meta.label}</span></td>
                    <td className="text-right">
                      <Link to={`/invoices/${r.invoice_id}`} data-testid={`open-${r.invoice_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#C1121F] hover:text-[#A00F19]">
                        Collect <ArrowRight size={12}/>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
